// frontend/src/features/livestock/AssignBottomBar.tsx
/**
 * The assign view's bar on a phone. Normally fixed to the bottom: what was
 * just sorted, the organism picked (or recent ones), the search field and the
 * actions. While mounted it sets --rdh-bottom-bar-h to its height (safe-area
 * padding included) so the upload strip sits above it, and reports the height
 * for an in-flow spacer.
 *
 * Search mode: the same element becomes a layer over the whole visible area,
 * so the search field keeps its focus (and the keyboard stays up) as the
 * layout changes. iOS doesn't shrink the layout viewport for the keyboard
 * (only visualViewport), so the layer's top and height follow visualViewport
 * through --rdh-vv-top and --rdh-vv-height. The page behind doesn't scroll,
 * and html[data-photo-search] hides the upload strip.
 */

import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { Box } from '@mantine/core';
import classes from './AssignPhotoView.module.css';

export interface AssignBottomBarProps {
  searching: boolean;
  onHeight: (height: number) => void;
  children: ReactNode;
}

/** Keeps the element's --rdh-vv-top and --rdh-vv-height on the visible area while active. */
function useVisibleArea(ref: React.RefObject<HTMLElement>, active: boolean): void {
  useLayoutEffect(() => {
    const element = ref.current;
    if (!active || !element) return undefined;
    const viewport = window.visualViewport;
    const update = (): void => {
      element.style.setProperty('--rdh-vv-top', `${viewport ? viewport.offsetTop : 0}px`);
      element.style.setProperty(
        '--rdh-vv-height',
        `${viewport ? viewport.height : window.innerHeight}px`
      );
    };
    update();
    const target: EventTarget = viewport ?? window;
    target.addEventListener('resize', update);
    viewport?.addEventListener('scroll', update);
    return () => {
      target.removeEventListener('resize', update);
      viewport?.removeEventListener('scroll', update);
      element.style.removeProperty('--rdh-vv-top');
      element.style.removeProperty('--rdh-vv-height');
    };
  }, [ref, active]);
}

/** No page scroll behind the layer; back where it was afterwards. */
function useScrollLock(active: boolean): void {
  useLayoutEffect(() => {
    if (!active) return undefined;
    const root = document.documentElement;
    const { body } = document;
    const scrollY = window.scrollY;
    const before = { root: root.style.overflow, body: body.style.overflow };
    root.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    root.setAttribute('data-photo-search', '');
    return () => {
      root.style.overflow = before.root;
      body.style.overflow = before.body;
      root.removeAttribute('data-photo-search');
      window.scrollTo(0, scrollY);
    };
  }, [active]);
}

export function AssignBottomBar({
  searching,
  onHeight,
  children,
}: AssignBottomBarProps): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const report = useRef(onHeight);
  report.current = onHeight;
  const searchingRef = useRef(searching);
  searchingRef.current = searching;

  useLayoutEffect(() => {
    const bar = ref.current;
    if (!bar) return undefined;
    const root = document.documentElement;
    const update = (): void => {
      // As a layer it covers the screen; the strip is hidden and the spacer keeps its height.
      if (searchingRef.current) return;
      const height = bar.offsetHeight;
      root.style.setProperty('--rdh-bottom-bar-h', `${height}px`);
      report.current(height);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(bar);
    return () => {
      observer.disconnect();
      root.style.removeProperty('--rdh-bottom-bar-h');
    };
  }, []);

  useVisibleArea(ref, searching);
  useScrollLock(searching);

  return (
    <Box ref={ref} className={classes.bar} data-bottom-bar data-searching={searching || undefined}>
      {children}
    </Box>
  );
}
